import { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Grid,
    Portal,
    Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useDocumentsStoreMutation, useUserProfileQuery } from 'app/store';
import { LoadingButton } from '@mui/lab';
import { useNavigate } from 'react-router-dom';

import AnimatedLayout from 'components/layouts/animated-layout';
import { Dialog } from 'components/dialog';
import DocumentUploaderComponent from './document-uploader';
import { handleServerErrors } from 'utils/utils';

const uploadUrl = `${String(import.meta.env.VITE_APP_API_URL || '').replace(/\/?$/, '/')}user/me/documents/upload`;
const MAX_DOCUMENT_SIZE = 10 * 1024 * 1024;

type DocumentName =
    | 'passport'
    | 'national'
    | 'selfie'
    | 'statute'
    | 'paper';

const emptyLoadingState: Record<DocumentName, boolean> = {
    passport: false,
    national: false,
    selfie: false,
    statute: false,
    paper: false,
};

const ImportantNotes = ({ isPerson }: { isPerson: boolean }) => (
    <Box
        sx={(theme) => ({
            mb: 2,
            p: { xs: 1.5, sm: 1.75 },
            border: '1px solid',
            borderColor: alpha(theme.palette.info.main, 0.24),
            borderRadius: 1.5,
            backgroundColor: alpha(
                theme.palette.info.main,
                theme.palette.mode === 'dark' ? 0.08 : 0.045,
            ),
        })}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
            <i className="fa-light fa-circle-info" />
            <Typography sx={{ fontSize: 13, fontFamily: 'YekanBalkhHeavy' }}>
                نکات مهم قبل از ارسال مدارک
            </Typography>
        </Box>

        <Box
            sx={{
                display: 'grid',
                gap: 0.65,
                color: 'text.primary',
                '& .register-document-note': {
                    fontSize: 11.25,
                    lineHeight: 1.95,
                },
            }}>
            <Typography className="register-document-note">
                * حداقل سن برای دریافت درگاه{' '}
                <Box component="span" sx={{ fontFamily: 'YekanBalkhHeavy' }}>
                    18 سال تمام
                </Box>{' '}
                است.
            </Typography>
            <Typography className="register-document-note">
                * امکان استفاده از مدارک دیگران وجود ندارد و در صورت مشاهده، حساب
                کاربری مسدود می‌گردد.
            </Typography>
            <Typography className="register-document-note">
                {isPerson
                    ? '* جهت استفاده از تمامی خدمات وبسایت باید هویت خود را تایید کنید. جهت تایید هویت، ارسال تصویر شناسنامه و کارت ملی الزامی می‌باشد.'
                    : '* جهت استفاده از تمامی خدمات وبسایت باید هویت شخصیت حقوقی خود را تایید کنید. جهت تایید هویت شخصیت حقوقی، ارسال اسکن اساسنامه، آخرین روزنامه آگهی شده، کارت ملی و شناسنامه مدیرعامل الزامی می‌باشد.'}
            </Typography>
            <Typography className="register-document-note">
                * مدارک جعلی و ساختگی و یا استفاده از مدارک دیگران جرم محسوب می‌گردد
                و مدارک ارسالی پس از بررسی کارشناسان و در صورت احراز تقلبی بودن به
                مراجع قضایی گزارش خواهد شد.
            </Typography>
            <Typography className="register-document-note">
                * مجازات جعل اسناد طبق ماده 530 قانون تعزیرات، 2 ماه تا 2 سال حبس
                به همراه جزای نقدی می‌باشد.
            </Typography>
        </Box>
    </Box>
);

const SelfieInstructions = () => (
    <Box
        sx={(theme) => ({
            mt: 2.25,
            mb: 1.5,
            p: { xs: 1.5, sm: 1.75 },
            border: '1px solid',
            borderColor: theme.palette.divider,
            borderRadius: 1.5,
            backgroundColor:
                theme.palette.mode === 'dark'
                    ? alpha(theme.palette.common.white, 0.018)
                    : alpha(theme.palette.primary.main, 0.018),
        })}>
        <Typography sx={{ fontSize: 13, fontFamily: 'YekanBalkhHeavy', mb: 1 }}>
            تصویر شما به همراه مدرک و دست‌نوشته
        </Typography>
        <Box sx={{ display: 'grid', gap: 0.65 }}>
            <Typography sx={{ fontSize: 11.25, lineHeight: 1.95 }}>
                * متن زیر را با خط خوانا بر روی کاغذ بنویسید و در پایان نام و نام
                خانوادگی، امضا و تاریخ را درج کنید.
            </Typography>
            <Typography sx={{ fontSize: 11.25, lineHeight: 1.95 }}>
                * یک کارت شناسایی معتبر شامل{' '}
                <Box component="span" sx={{ fontFamily: 'YekanBalkhHeavy' }}>
                    کارت ملی هوشمند، گواهینامه یا کارت پایان خدمت
                </Box>{' '}
                در کنار دست‌نوشته قرار دهید.
            </Typography>
            <Typography sx={{ fontSize: 11.25, lineHeight: 1.95 }}>
                * در حالی که کارت شناسایی و دست‌نوشته را در دست دارید، یک عکس واضح
                از خودتان تهیه کنید.
            </Typography>
        </Box>
    </Box>
);

const PledgeText = () => (
    <Box
        sx={(theme) => ({
            mb: 1.5,
            p: { xs: 1.5, sm: 1.75 },
            borderRadius: 1.5,
            border: '1px solid',
            borderColor: alpha(theme.palette.primary.main, 0.24),
            backgroundColor: alpha(
                theme.palette.primary.main,
                theme.palette.mode === 'dark' ? 0.08 : 0.045,
            ),
        })}>
        <Typography sx={{ fontSize: 12.5, fontFamily: 'YekanBalkhHeavy' }}>
            متن فرم احراز — متن زیر را عیناً بنویسید
        </Typography>
        <Typography sx={{ mt: 0.8, fontSize: 11.25, lineHeight: 2.05 }}>
            اینجانب ............... قوانین و شرایط استفاده از آقای پرداخت را مطالعه
            کرده و پذیرفته‌ام و متعهد می‌شوم از حساب کاربری خود در آقای پرداخت شخصا
            و در چارچوب قوانین کشور استفاده کنم. حسابم را در اختیار غیر قرار ندهم و
            به نمایندگی از دیگران از حساب کاربری‌ام استفاده نکنم.
        </Typography>
        <Typography sx={{ mt: 0.65, fontSize: 11.5, fontFamily: 'YekanBalkhHeavy' }}>
            نام و نام خانوادگی، امضا و تاریخ
        </Typography>
    </Box>
);

const GuideList = ({
    title,
    items,
    type,
}: {
    title: string;
    items: string[];
    type: 'success' | 'error';
}) => (
    <Box
        sx={(theme) => {
            const palette =
                type === 'success' ? theme.palette.success : theme.palette.error;
            return {
                p: { xs: 1.5, sm: 1.75 },
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: alpha(palette.main, 0.24),
                backgroundColor: alpha(
                    palette.main,
                    theme.palette.mode === 'dark' ? 0.09 : 0.055,
                ),
            };
        }}>
        <Typography
            sx={{
                fontSize: 12.5,
                fontFamily: 'YekanBalkhHeavy',
                mb: 0.75,
                color: type === 'success' ? 'success.main' : 'error.main',
            }}>
            {title}
        </Typography>
        <Box sx={{ display: 'grid', gap: 0.45 }}>
            {items.map((item) => (
                <Typography key={item} sx={{ fontSize: 11, lineHeight: 1.85 }}>
                    {type === 'success' ? '✓' : '×'} {item}
                </Typography>
            ))}
        </Box>
    </Box>
);

const IdentityGuideDialog = ({
    open,
    onClose,
}: {
    open: boolean;
    onClose: () => void;
}) => (
    <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        label="راهنمای تهیه عکس احراز هویت"
        icon={
            <Box
                component="i"
                className="fa-light fa-id-card"
                sx={(theme) => ({
                    color:
                        theme.palette.mode === 'dark'
                            ? theme.palette.primary.light
                            : theme.palette.primary.main,
                })}
            />
        }>
            <Box
                sx={(theme) => ({
                    mb: 2,
                    p: { xs: 1.5, sm: 1.75 },
                    borderRadius: 1.5,
                    border: '1px solid',
                    borderColor: alpha(theme.palette.info.main, 0.24),
                    backgroundColor: alpha(
                        theme.palette.info.main,
                        theme.palette.mode === 'dark' ? 0.08 : 0.05,
                    ),
                })}>
                <Typography sx={{ fontSize: 13, fontFamily: 'YekanBalkhHeavy', mb: 0.75 }}>
                    عکس احراز هویت باید چگونه باشد؟
                </Typography>
                <Typography sx={{ fontSize: 11.25, lineHeight: 2 }}>
                    عکس را به صورتی تهیه کنید که{' '}
                    <Box component="span" sx={{ fontFamily: 'YekanBalkhHeavy' }}>
                        چهره شما، کارت شناسایی و متن دست‌نوشته
                    </Box>{' '}
                    همگی به صورت همزمان و واضح در تصویر دیده شوند.
                </Typography>
                <Typography sx={{ mt: 0.65, fontSize: 11.25, lineHeight: 2 }}>
                    کارت شناسایی را در یک دست و برگه تعهدنامه را در دست دیگر نگه دارید
                    و تصویر را در محیطی با نور مناسب تهیه کنید.
                </Typography>
                <Typography sx={{ mt: 0.65, fontSize: 11.25, lineHeight: 2, fontFamily: 'YekanBalkhHeavy' }}>
                    تمامی موارد باید در یک عکس و بدون برش قابل مشاهده باشند.
                </Typography>
            </Box>

            <Grid container spacing={1.5} sx={{ mb: 2 }}>
                <Grid item xs={12} md={6}>
                    <Box
                        sx={{
                            height: '100%',
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1.5,
                            overflow: 'hidden',
                        }}>
                        <Box sx={{ p: 1.25, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                            <Typography sx={{ fontSize: 12.5, fontFamily: 'YekanBalkhHeavy' }}>
                                نمونه آقایان
                            </Typography>
                            <Typography sx={{ fontSize: 10.5, color: 'success.main', fontFamily: 'YekanBalkhHeavy' }}>
                                نمونه صحیح ✓
                            </Typography>
                        </Box>
                        <Box
                            component="img"
                            src="/images/register/identity-example-man.jpg"
                            alt="نمونه صحیح عکس احراز هویت آقایان"
                            sx={{ width: '100%', display: 'block', maxHeight: 480, objectFit: 'cover' }}
                        />
                        <Typography sx={{ p: 1.25, fontSize: 11, lineHeight: 1.9 }}>
                            چهره، کارت شناسایی و دست‌نوشته باید به صورت کامل و واضح در یک قاب قرار گرفته باشند.
                        </Typography>
                    </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Box
                        sx={{
                            height: '100%',
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1.5,
                            overflow: 'hidden',
                        }}>
                        <Box sx={{ p: 1.25, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                            <Typography sx={{ fontSize: 12.5, fontFamily: 'YekanBalkhHeavy' }}>
                                نمونه بانوان
                            </Typography>
                            <Typography sx={{ fontSize: 10.5, color: 'success.main', fontFamily: 'YekanBalkhHeavy' }}>
                                نمونه صحیح ✓
                            </Typography>
                        </Box>
                        <Box
                            component="img"
                            src="/images/register/identity-example-woman.jpg"
                            alt="نمونه صحیح عکس احراز هویت بانوان"
                            sx={{ width: '100%', display: 'block', maxHeight: 480, objectFit: 'cover' }}
                        />
                        <Typography sx={{ p: 1.25, fontSize: 11, lineHeight: 1.9 }}>
                            صورت باید کاملاً مشخص بوده و کارت شناسایی و متن دست‌نوشته نیز بدون پوشیدگی در تصویر دیده شوند.
                        </Typography>
                    </Box>
                </Grid>
            </Grid>

            <Grid container spacing={1.5}>
                <Grid item xs={12} md={6}>
                    <GuideList
                        title="شرایط یک تصویر صحیح"
                        type="success"
                        items={[
                            'چهره شما کاملاً واضح و مشخص باشد.',
                            'کارت شناسایی معتبر در دست شما باشد.',
                            'متن دست‌نوشته کاملاً قابل خواندن باشد.',
                            'نام و نام خانوادگی، امضا و تاریخ درج شده باشد.',
                            'تصویر در محیط روشن و با کیفیت مناسب تهیه شود.',
                            'تمامی موارد در یک عکس و یک قاب قرار داشته باشند.',
                        ]}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <GuideList
                        title="مواردی که باعث رد تصویر می‌شوند"
                        type="error"
                        items={[
                            'تار، کم‌نور یا بی‌کیفیت بودن تصویر',
                            'ناخوانا بودن متن دست‌نوشته',
                            'مشخص نبودن کامل صورت',
                            'بریده شدن بخشی از کارت یا دست‌نوشته',
                            'ارسال تصویر کارت شناسایی به تنهایی',
                            'استفاده از تصویر یا مدارک شخص دیگری',
                        ]}
                    />
                </Grid>
            </Grid>
    </Dialog>
);

export const DocumentsFormStep = (): JSX.Element => {
    const { data: profile } = useUserProfileQuery();
    const [documentsStore, { isLoading: apiIsLoading }] =
        useDocumentsStoreMutation();
    const [passportFile, updatePassportFile] = useState<string>('');
    const [nationalFile, updateNationalFile] = useState<string>('');
    const [selfieFile, updateSelfieFile] = useState<string>('');
    const [statuteFile, updateStatuteFile] = useState<string>('');
    const [paperFile, updatePaperFile] = useState<string>('');
    const [guideOpen, setGuideOpen] = useState(false);
    const [isLoading, setLoading] =
        useState<Record<DocumentName, boolean>>(emptyLoadingState);

    const navigate = useNavigate();
    const isPerson = profile?.type === 'person';

    const handleOnSubmit = () => {
        const data = isPerson
            ? {
                  national: nationalFile,
                  passport: passportFile,
                  selfie: selfieFile,
              }
            : {
                  national: nationalFile,
                  passport: passportFile,
                  statute: statuteFile,
                  paper: paperFile,
              };

        documentsStore(data)
            .unwrap()
            .then(() => {
                navigate('/register/waiting');
            })
            .catch((error: any) => {
                handleServerErrors(error);
            });
    };

    useEffect(() => {
        const documents = profile?.opts?.documents;
        if (!documents) return;

        updateNationalFile(documents.national || '');
        updatePassportFile(documents.passport || '');
        updateSelfieFile(documents.selfie || '');
        updatePaperFile(documents.paper || '');
        updateStatuteFile(documents.statute || '');
    }, [profile?.opts?.documents]);

    useEffect(() => {
        if (isPerson) {
            updateStatuteFile('');
            updatePaperFile('');
        } else {
            updateSelfieFile('');
        }
    }, [isPerson]);

    useEffect(() => {
        if (Object.values(isLoading).some(Boolean)) {
            setLoading(emptyLoadingState);
        }
    }, [passportFile, nationalFile, selfieFile, statuteFile, paperFile]);

    const requiredFilesReady = isPerson
        ? Boolean(nationalFile && passportFile && selfieFile)
        : Boolean(nationalFile && passportFile && statuteFile && paperFile);

    const imageFormats = ['image/jpeg', 'image/png'];
    const imageExtensions = ['.jpg', '.jpeg', '.png'];
    const companyFormats = [...imageFormats, 'application/pdf'];
    const companyExtensions = [...imageExtensions, '.pdf'];

    return (
        <AnimatedLayout>
            <Portal
                container={() =>
                    document.getElementById('register-documents-header-action')
                }>
                <LoadingButton
                    variant="contained"
                    loading={apiIsLoading || Object.values(isLoading).some(Boolean)}
                    disabled={!requiredFilesReady}
                    onClick={handleOnSubmit}
                    sx={{ minWidth: { xs: 96, sm: 140 }, minHeight: { xs: 36, sm: 40 } }}>
                    تایید و ادامه
                </LoadingButton>
            </Portal>

            {Boolean(profile?.comments?.trim()) && (
                <Alert
                    severity="error"
                    variant="outlined"
                    sx={{
                        mb: 1.5,
                        py: 0.65,
                        alignItems: 'center',
                        '& .MuiAlert-message': { width: '100%' },
                    }}>
                    <Typography sx={{ fontSize: 12, lineHeight: 1.9 }}>
                        <Box
                            component="span"
                            sx={{ fontFamily: 'YekanBalkhHeavy', ml: 0.5 }}>
                            علت رد مدارک:
                        </Box>
                        {profile?.comments}
                    </Typography>
                </Alert>
            )}

            <ImportantNotes isPerson={isPerson} />

            <Box sx={{ mb: 1.25 }}>
                <Typography sx={{ fontSize: 13.5, fontFamily: 'YekanBalkhHeavy' }}>
                    {isPerson ? 'مدارک شناسایی' : 'مدارک شخصیت حقوقی'}
                </Typography>
                <Typography sx={{ mt: 0.2, fontSize: 10.75, color: 'text.secondary' }}>
                    هر فایل حداکثر ۱۰ مگابایت باشد.
                </Typography>
            </Box>

            <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6} lg={isPerson ? 6 : 3}>
                    <DocumentUploaderComponent
                        name="national"
                        label={isPerson ? 'کارت ملی' : 'کارت ملی امضادار'}
                        description={
                            isPerson
                                ? 'تصویر واضح روی کارت ملی'
                                : 'تصویر کارت ملی مدیرعامل / دارنده حق امضا با امضا'
                        }
                        formatsLabel={isPerson ? 'JPG / JPEG / PNG' : 'PDF / JPG / JPEG / PNG'}
                        uploadUrl={uploadUrl}
                        maxSize={MAX_DOCUMENT_SIZE}
                        allowedFormats={isPerson ? imageFormats : companyFormats}
                        allowedExtensions={isPerson ? imageExtensions : companyExtensions}
                        isLoading={isLoading}
                        setLoading={setLoading}
                        file={nationalFile}
                        updateFile={updateNationalFile}
                    />
                </Grid>

                <Grid item xs={12} sm={6} lg={isPerson ? 6 : 3}>
                    <DocumentUploaderComponent
                        name="passport"
                        label={isPerson ? 'شناسنامه' : 'شناسنامه امضادار'}
                        description={
                            isPerson
                                ? 'صفحه مشخصات شناسنامه، کامل و خوانا'
                                : 'صفحه مشخصات شناسنامه مدیرعامل / دارنده حق امضا با امضا'
                        }
                        formatsLabel={isPerson ? 'JPG / JPEG / PNG' : 'PDF / JPG / JPEG / PNG'}
                        uploadUrl={uploadUrl}
                        maxSize={MAX_DOCUMENT_SIZE}
                        allowedFormats={isPerson ? imageFormats : companyFormats}
                        allowedExtensions={isPerson ? imageExtensions : companyExtensions}
                        isLoading={isLoading}
                        setLoading={setLoading}
                        file={passportFile}
                        updateFile={updatePassportFile}
                    />
                </Grid>

                {!isPerson && (
                    <>
                        <Grid item xs={12} sm={6} lg={3}>
                            <DocumentUploaderComponent
                                name="statute"
                                label="اساسنامه"
                                description="آخرین نسخه معتبر اساسنامه شرکت"
                                formatsLabel="PDF / JPG / JPEG / PNG"
                                uploadUrl={uploadUrl}
                                maxSize={MAX_DOCUMENT_SIZE}
                                allowedFormats={companyFormats}
                                allowedExtensions={companyExtensions}
                                isLoading={isLoading}
                                setLoading={setLoading}
                                file={statuteFile}
                                updateFile={updateStatuteFile}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} lg={3}>
                            <DocumentUploaderComponent
                                name="paper"
                                label="آگهی تغییرات"
                                description="آخرین آگهی تغییرات معتبر شرکت"
                                formatsLabel="PDF / JPG / JPEG / PNG"
                                uploadUrl={uploadUrl}
                                maxSize={MAX_DOCUMENT_SIZE}
                                allowedFormats={companyFormats}
                                allowedExtensions={companyExtensions}
                                isLoading={isLoading}
                                setLoading={setLoading}
                                file={paperFile}
                                updateFile={updatePaperFile}
                            />
                        </Grid>
                    </>
                )}
            </Grid>

            {isPerson && (
                <>
                    <SelfieInstructions />
                    <PledgeText />

                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            mb: 1.5,
                        }}>
                        <Button
                            variant="outlined"
                            size="medium"
                            startIcon={<i className="fa-light fa-image" />}
                            onClick={() => setGuideOpen(true)}
                            sx={(theme) => ({
                                color: theme.palette.text.primary,
                                borderColor:
                                    theme.palette.mode === 'dark'
                                        ? 'rgba(255,255,255,.22)'
                                        : theme.palette.divider,
                                backgroundColor: theme.palette.background.paper,
                                '& .MuiButton-startIcon': { color: 'inherit' },
                                '&:hover': {
                                    color: theme.palette.text.primary,
                                    borderColor:
                                        theme.palette.mode === 'dark'
                                            ? 'rgba(255,255,255,.38)'
                                            : theme.palette.grey[400],
                                    backgroundColor:
                                        theme.palette.mode === 'dark'
                                            ? 'rgba(255,255,255,.04)'
                                            : theme.palette.action.hover,
                                },
                            })}>
                            مشاهده نمونه صحیح عکس احراز هویت
                        </Button>
                    </Box>

                    <Box sx={{ maxWidth: 760, mx: 'auto' }}>
                        <DocumentUploaderComponent
                            name="selfie"
                            label="تصویر به همراه مدرک و دست‌نوشته"
                            description="چهره، کارت شناسایی و متن دست‌نوشته در یک تصویر واضح و بدون برش باشند"
                            formatsLabel="JPG / JPEG / PNG"
                            uploadUrl={uploadUrl}
                            maxSize={MAX_DOCUMENT_SIZE}
                            allowedFormats={imageFormats}
                            allowedExtensions={imageExtensions}
                            isLoading={isLoading}
                            setLoading={setLoading}
                            file={selfieFile}
                            updateFile={updateSelfieFile}
                        />
                    </Box>

                    <IdentityGuideDialog
                        open={guideOpen}
                        onClose={() => setGuideOpen(false)}
                    />
                </>
            )}
        </AnimatedLayout>
    );
};
